import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { login, isAuthenticated } from '../services/auth';
import securityImage from '../assets/security.webp'; // Using WebP format

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [showMagicWordMessage, setShowMagicWordMessage] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get redirect path from location state or default to home
  const from = location.state?.from?.pathname || '/';
  
  // Check if already authenticated and redirect if needed
  useEffect(() => {
    if (isAuthenticated()) {
      navigate(from, { replace: true });
    }
  }, [from, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple client-side validation
    if (!username.trim() || !password.trim()) {
      setError('Username and password are required');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      setAttempts(prev => prev + 1);
      
      // Show the magic word message after failed attempts
      if (attempts >= 1) {
        setShowMagicWordMessage(true);
        setTimeout(() => setShowMagicWordMessage(false), 3000);
      }
      
      setError(typeof err === 'string' ? err : 'Authentication failed. Please check your credentials.');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };
  
  // Optional: Play sound effect (need to be careful with copyright)
  const playDeniedSound = () => {
    const audio = new Audio('/magic-word.mp3'); // Create this sound or use a free alternative
    audio.volume = 0.5;
    audio.play().catch(e => console.log('Audio playback prevented:', e));
  };
  
  useEffect(() => {
    if (showMagicWordMessage) {
      playDeniedSound();
    }
  }, [showMagicWordMessage]);
  
  return (
    <div className="login-page d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
      <Card className="shadow" style={{ width: '100%', maxWidth: '450px' }}>
        <Card.Header className="bg-primary text-white text-center py-3">
          <h4 className="mb-0">Signal Monkey</h4>
        </Card.Header>
        <Card.Body className="p-4">
          {showMagicWordMessage ? (
            <div className="text-center mb-4">
              <img 
                src={securityImage} 
                alt="Security check" 
                style={{ maxWidth: '100%', height: '200px' }} 
                className="mb-3" 
              />
              <h5 className="text-danger">Ah ah ah! You didn't say the magic word!</h5>
            </div>
          ) : (
            <h5 className="text-center mb-4">Log In</h5>
          )}
          
          {error && !showMagicWordMessage && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                autoFocus
              />
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </Form.Group>
            
            <div className="d-grid">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading}
                className="py-2"
              >
                {loading ? (
                  <>
                    <Spinner 
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Signing in...
                  </>
                ) : 'Sign In'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default LoginPage;